import { resolveId } from '@medplum/core'

interface PatientScopedSegmentLookup {
  [key: string]: string
}

// Patient-scoped detail route segment per resource type. Mirrors the deep-link
// paths used across the patient UI (see useTimelineItemUrl, MedicationsTable,
// CarePlanHistoryPage, etc.) so links land on the same pages.
const PATIENT_SCOPED_SEGMENT: PatientScopedSegmentLookup = {
  Task: 'tasks',
  ServiceRequest: 'orders',
  MedicationRequest: 'medications',
  CarePlan: 'care-plans',
  Condition: 'conditions',
  Observation: 'observations',
  DocumentReference: 'documents',
  Composition: 'visit-notes',
  Assessment: 'assessments',
}

// Build an in-app deep link to a resource's detail page. Most clinical
// resources live under their patient (`/Patient/{id}/{segment}/{resourceId}`);
// Patient and Practitioner have their own top-level routes. Returns null when
// no route is known or the patient context is missing for a patient-scoped
// type — callers render plain text in that case.
export function resourceDeepLink({
  resourceType,
  resourceId,
  patientReference,
}: {
  resourceType: string
  resourceId: string | undefined
  patientReference?: string
}): string | null {
  const patientId = resolveId({ reference: patientReference })
  if (!resourceId) {
    return null
  }
  if (resourceType === 'Patient') {
    return `/Patient/${resourceId}`
  }
  if (resourceType === 'Practitioner') {
    return `/practitioners/${resourceId}`
  }
  if (resourceType === 'Appointment') {
    return patientId ? `/Patient/${patientId}/appointments#${resourceId}` : null
  }
  const segment = PATIENT_SCOPED_SEGMENT[resourceType]
  if (!segment || !patientId) {
    return null
  }
  return `/Patient/${patientId}/${segment}/${resourceId}`
}
